import "./document-viewer-flyout.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { ListView } from "@tql/ui-components-react/kendo/listview";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { PDFViewer } from "@tql/ui-components-react/tql/pdf-viewer";
import type { ReactNode } from "react";
import { memo } from "react";

import type { DefaultDocument } from "~/shared/types/payment-types.ts";

import { BaseFlyout } from "../flyout/base-flyout.tsx";
import { LoadingSpinner } from "../loading-spinner/loading-spinner.tsx";
import type { DocumentViewerState } from "./hooks/use-document-viewer";
import type { DocumentViewerSelectionHandlers } from "./hooks/use-document-viewer-selection.ts";

export type DocumentViewerFlyoutProps = DocumentViewerSelectionHandlers & {
	viewerState: DocumentViewerState;
	closeViewer: () => void;
	documents: DefaultDocument[] | null;
	title?: string;
	contentData?: string;
	isLoadingDocumentList?: boolean;
	isLoadingInvoiceDocument?: boolean;
	isDownloading?: boolean;
	isPrinting?: boolean;
	/** Additional feature-specific buttons rendered before Download/Print (e.g. shipment edit's document generation action). */
	extraHeaderActions?: ReactNode;
	isMobile?: boolean;
};

export const DocumentViewerFlyout = memo(
	({
		viewerState,
		closeViewer,
		documents,
		title = "Documents",
		contentData,
		isLoadingDocumentList,
		isLoadingInvoiceDocument,
		isDownloading,
		isPrinting,
		extraHeaderActions,
		itemRenderer,
		handlePrintClick,
		handleDownloadClick,
		isNoneSelected,
		isMobile,
	}: DocumentViewerFlyoutProps) => {
		const headerActions = (
			<>
				{extraHeaderActions}
				<Button
					disabled={
						isNoneSelected || (isDownloading ?? false) || (isPrinting ?? false)
					}
					id={"borderless-button"}
					onClick={handleDownloadClick}
				>
					{isDownloading ? "Downloading..." : "Download Selected"}
				</Button>
				<Button
					disabled={
						isNoneSelected || (isDownloading ?? false) || (isPrinting ?? false)
					}
					id={"borderless-button"}
					onClick={handlePrintClick}
				>
					{isPrinting ? "Preparing..." : "Print Selected"}
				</Button>
			</>
		);

		return (
			<BaseFlyout
				headerActions={headerActions}
				isMobile={isMobile}
				isOpen={viewerState.isOpen}
				onClose={closeViewer}
				title={title}
			>
				<div className={"flyout-content-wrapper"}>
					<div className={"flyout-listview-wrapper"}>
						{isLoadingDocumentList ? (
							<LoadingSpinner />
						) : (
							<ListView
								data={documents ?? []}
								item={itemRenderer}
								style={{
									height: "100%",
									backgroundColor: "var(--kendo-color-base)",
								}}
							/>
						)}
					</div>
					<div className={"flyout-pdf-viewer-wrapper"}>
						{isLoadingInvoiceDocument ? (
							<div className={"flyout-loader-wrapper"}>
								<Loader
									size={"large"}
									themeColor={"primary"}
									type={"infinite-spinner"}
								/>
							</div>
						) : (
							<PDFViewer
								data={contentData || ""}
								style={{ width: "100%", height: "100%", maxWidth: "100%" }}
								tools={[
									"pager",
									"spacer",
									"zoomInOut",
									"zoom",
									"selection",
									"spacer",
									"search",
								]}
							/>
						)}
					</div>
				</div>
			</BaseFlyout>
		);
	},
);

DocumentViewerFlyout.displayName = "DocumentViewerFlyout";
